import { makeAutoObservable, runInAction } from "mobx";
import axios from "axios";
import { Compte } from "@/types/app";
import { storage } from "@/lib/storage";
import { AppConfig } from "@/config/AppConfig";
import { directusService } from "./directus";

export class CoreController {
  private static _instance: CoreController;

  appSettingResponse: {
    compte: Compte[] | null;
    whatsapp_support_link?: string;
    banner?: any;
    apiKey?: string;
    idFtxOrange?: string;
    idFtxMoov?: string;
    name?: string;
    version?: string;
    config?: {
      RUE: string;
      OM_PAYEMENT_NUMBER: string;
      MOOV_PAYEMENT_NUMBER: string;
      APP_NAME: string;
      WITHDRAW_RUE: {}
    };
  } = { compte: null };
  compte: Compte = { id: "", type: "" };
  commercialId: string = "";
  loading = false;
  error: string | null = null;

  private constructor() {
    makeAutoObservable(this);

    // Load and validate environment variables
    this._loadEnvironmentVariables();
    this._validateSecurity();

    // Initialize commercial ID from storage
    const storedCommercialId = storage.read<string>("commercial_id");
    if (storedCommercialId) {
      this.commercialId = storedCommercialId;
    }
  }

  // Load environment variables
  private _loadEnvironmentVariables() {
    AppConfig.log(`Base URL: ${AppConfig.baseUrl}`);
    AppConfig.log(`Application ID: ${AppConfig.applicationId}`);

    // Validate configuration
    AppConfig.validateApiConfiguration();
  }

  // Security validation
  private _validateSecurity() {
    if (AppConfig.isDebugMode) {
      AppConfig.log("Running in debug mode");
    } else {
      AppConfig.log("Running in production mode");
    }
  }

  static get instance(): CoreController {
    if (!CoreController._instance) {
      CoreController._instance = new CoreController();
    }
    return CoreController._instance;
  }

  async loadApplicationSetting() {
    console.debug("🔄 [CoreController] Starting loadApplicationSetting...");
    console.debug(
      "🔄 [CoreController] API URL:",
      `${AppConfig.baseUrl}/items/mobcashapplications/${AppConfig.applicationId}?fields[]=*.*`
    );

    this.loading = true;
    this.error = null;

    try {
      const response = await axios.get(
        `${AppConfig.baseUrl}/items/mobcashapplications/${AppConfig.applicationId}?fields[]=*.*`
      );

      console.debug(
        "✅ [CoreController] Application settings loaded from Directus:",
        response.data
      );

      runInAction(() => {
        const data = response.data.data;
        console.debug("📦 [CoreController] Extracted data:", data);

        // Vérifier et logger les données de bannière
        console.debug("🎯 [CoreController] Banner data:", {
          hasBanner: !!data.banner,
          bannerData: data.banner,
          bannerIsActive: data.banner?.is_active
        });

        this.appSettingResponse = {
          compte: data.compte || [],
          whatsapp_support_link: data.whatsapp_support_link,
          banner: data.banner,
          apiKey: data.api_key,
          idFtxOrange: data.id_ftx_orange?.id,
          idFtxMoov: data.id_ftx_moov?.id,
          name: data.name,
          version: data.version,
          config: data.config,
        };

        console.debug("✅ [CoreController] Configuration loaded successfully:", {
          idFtxOrange: this.appSettingResponse.idFtxOrange,
          idFtxMoov: this.appSettingResponse.idFtxMoov,
          compte: this.appSettingResponse.compte,
          whatsapp: this.appSettingResponse.whatsapp_support_link,
          banner: this.appSettingResponse.banner,
        });

        // Configure DirectusService with API key (comme Dio dans Flutter)
        if (this.appSettingResponse.apiKey) {
          console.debug(
            "✅ [CoreController] DirectusService configured with API key"
          );
        }

        // Sort accounts - 1XBET first, then others (comme dans l'app Flutter)
        if (this.appSettingResponse.compte) {
          const sortedAccounts = [...this.appSettingResponse.compte].sort(
            (a, b) => {
              if (a.type === "1XBET") return -1;
              if (b.type === "1XBET") return 1;
              return 0;
            }
          );
          this.appSettingResponse.compte = sortedAccounts;

          // Set default account if not already selected
          if (!this.compte.id && sortedAccounts.length > 0) {
            this.compte = sortedAccounts[0];
          }
        }
      });
    } catch (error: any) {
      console.error("Error loading app settings:", error);

      runInAction(() => {
        this.error = "Failed to load application settings";

        // Ultimate fallback
        this.appSettingResponse = {
          compte: [
            { id: "1", type: "1XBET" },
            { id: "2", type: "BETWINNER" },
          ],
          whatsapp_support_link: "https://wa.me/22654963888",
        };

        if (
          !this.compte.id &&
          this.appSettingResponse.compte &&
          this.appSettingResponse.compte.length > 0
        ) {
          this.compte = this.appSettingResponse.compte[0];
        }
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  setAccount(account: Compte) {
    this.compte = account;
  }

  async checkWithdraw(amount: number): Promise<boolean> {
    try {
      if (
        !this.appSettingResponse.apiKey ||
        !this.appSettingResponse.idFtxOrange
      ) {
        console.error("API key or ID Orange missing");
        return true; // Fallback
      }

      const response = await axios.post(
        `${AppConfig.baseUrl}/pay/appbalance`,
        {
          app_id: this.appSettingResponse.idFtxOrange,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const solde = response.data.solde as number;
      console.debug("Current balance:", solde);
      return solde >= amount;
    } catch (error) {
      console.error("Error checking withdraw balance:", error);
      return true; // Fallback for now
    }
  }
  async checkBalanceForDeposit(
    amount: number,
    compteId: string
  ): Promise<boolean> {
    try {
      const response = await axios.post(
        `${AppConfig.baseUrl}/api/balance-check`,
        {
          compteId: compteId,
          amount:amount
        },
        {
          headers: {
            // Authorization: `Bearer ${this.appSettingResponse.apiKey}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data.canDeposit as boolean;
    } catch (error) {
      console.error("Error checking deposit balance:", error);
      return true; // Fallback for now
    }
  }

  get whatsappLink(): string {
    return (
      this.appSettingResponse.whatsapp_support_link ||
      "https://wa.me/22654963888"
    );
  }

  // Commercial ID Management
  setCommercialId(id: string | null) {
    if (id && id.trim()) {
      this.commercialId = id;
      storage.write("commercial_id", id);
      console.debug("Commercial ID set:", id);

      // Track in analytics if available
      if (window.gtag) {
        window.gtag("event", "commercial_id_set", { commercial_id: id });
      }
    }
  }

  getCommercialId(): string | null {
    return this.commercialId || storage.read<string>("commercial_id");
  }

  clearCommercialId() {
    this.commercialId = "";
    storage.remove("commercial_id");
    console.debug("Commercial ID cleared");
  }

  // Debug commercial ID state
  debugCommercialId() {
    console.debug("Current commercialId:", this.commercialId);
    console.debug("Stored commercialId:", storage.read("commercial_id"));
  }
}

// React hook for using the CoreController
export function useCoreController() {
  return CoreController.instance;
}
